import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import FileDrop from '../components/FileDrop';
import Panel from '../components/Panel';
import RecentFilesTable from '../components/RecentFilesTable';
import { converterConfig, currencies } from '../converters/config';

const formDataFor = (file, settings) => {
  const formData = new FormData();
  formData.append('excelFile', file);
  Object.entries(settings).forEach(([key, value]) => {
    formData.append(key, typeof value === 'boolean' ? String(value) : value);
  });
  return formData;
};

const loadSettings = (type, defaults) => {
  try {
    const saved = localStorage.getItem(`sfcc:settings:${type}`);
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    return Object.fromEntries(Object.keys(defaults).map((key) => [key, key in parsed ? parsed[key] : defaults[key]]));
  } catch (error) {
    return defaults;
  }
};

const ConverterPage = ({
  type,
  pendingFile,
  onPendingFileConsumed,
  onHistoryChanged,
  historyVersion
}) => {
  const navigate = useNavigate();
  const config = converterConfig[type];
  const [settings, setSettings] = useState(() => loadSettings(type, config.defaults));
  const settingsRef = useRef(settings);
  const checkSeq = useRef(0);
  const [savedSettings, setSavedSettings] = useState(() => Boolean(localStorage.getItem(`sfcc:settings:${type}`)));
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [result, setResult] = useState(null);
  const [convertError, setConvertError] = useState('');
  const [converting, setConverting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [previewTab, setPreviewTab] = useState('rows');
  const [recentFiles, setRecentFiles] = useState([]);

  useEffect(() => { settingsRef.current = settings; }, [settings]);

  const loadRecentFiles = useCallback(() => {
    axios.get('/api/files').then(response => setRecentFiles(response.data.files || []))
      .catch(() => setRecentFiles([]));
  }, []);

  useEffect(() => { loadRecentFiles(); }, [loadRecentFiles, historyVersion]);

  const performCheck = useCallback(async (selectedFile, values = settingsRef.current) => {
    const sequence = ++checkSeq.current;
    if (!selectedFile) {
      setChecking(false);
      return;
    }
    setChecking(true);
    setCheckError('');
    setCheck(null);
    try {
      const response = await axios.post(`/api/check/${type}`, formDataFor(selectedFile, values));
      if (sequence === checkSeq.current) setCheck(response.data);
    } catch (error) {
      if (sequence === checkSeq.current) {
        setCheckError(error.response?.data?.error || 'Unable to check this file.');
      }
    } finally {
      if (sequence === checkSeq.current) setChecking(false);
    }
  }, [type]);

  useEffect(() => {
    if (pendingFile && pendingFile !== file) {
      setFile(pendingFile);
      setResult(null);
      setConvertError('');
      onPendingFileConsumed();
    }
  }, [pendingFile, file, onPendingFileConsumed]);

  useEffect(() => {
    if (file) performCheck(file);
  }, [file, performCheck]);

  const changeSetting = (name, value) => {
    setSettings(current => ({ ...current, [name]: value }));
  };

  const handleFileChange = selectedFile => {
    setFile(selectedFile);
    setCheck(null);
    setResult(null);
    setConvertError('');
    setAttempted(false);
    setPreviewTab('rows');
  };

  const trimSettings = values => Object.fromEntries(Object.entries(values).map(([key, value]) => [
    key,
    typeof value === 'string' ? value.trim() : value
  ]));

  const handleConvert = async event => {
    event.preventDefault();
    setAttempted(true);
    setConvertError('');
    const trimmed = trimSettings(settings);
    setSettings(trimmed);
    settingsRef.current = trimmed;
    if (!file || checking || !check || check.errorCount > 0) return;
    if (config.required.some(name => !String(trimmed[name] || '').trim())) return;

    setConverting(true);
    try {
      const response = await axios.post(`/api/convert/${type}`, formDataFor(file, trimmed));
      setResult(response.data);
      localStorage.setItem(`sfcc:settings:${type}`, JSON.stringify(trimmed));
      setSavedSettings(true);
      onHistoryChanged();
    } catch (error) {
      setConvertError(error.response?.data?.error || 'Unable to convert this file.');
    } finally {
      setConverting(false);
    }
  };

  const convertAnother = () => {
    setFile(null);
    setCheck(null);
    setCheckError('');
    setConvertError('');
    setResult(null);
    setAttempted(false);
    setPreviewTab('rows');
  };

  const errorCount = check?.errorCount || 0;
  const readyCount = check ? check.total - errorCount : 0;
  const convertCount = type === 'catalog'
    ? (check?.kpis.find(item => item.label === 'Products')?.value || 0)
    : readyCount;
  const convertUnit = type === 'catalog' ? 'product' : 'row';
  const requiredError = name => attempted && config.required.includes(name) && !String(settings[name] || '').trim();
  const typeFiles = recentFiles.slice(0, 5);

  const renderField = field => {
    const value = settings[field.name];
    if (field.type === 'switch') {
      return (
        <Box key={field.name} sx={{ py: 0.5 }}>
          <FormControlLabel
            control={<Switch checked={Boolean(value)} onChange={event => changeSetting(field.name, event.target.checked)} />}
            label={field.label}
          />
          {field.helper && <FormHelperText sx={{ ml: 1.5, mt: -0.5 }}>{field.helper}</FormHelperText>}
        </Box>
      );
    }
    if (field.type === 'select') {
      return (
        <FormControl fullWidth key={field.name} error={requiredError(field.name)}>
          <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
          <Select
            label={`${field.label}${field.required ? ' *' : ''}`}
            value={value || ''}
            onChange={event => changeSetting(field.name, event.target.value)}
          >
            {currencies.map(currency => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
          </Select>
          {requiredError(field.name) && <FormHelperText>{field.label} is required</FormHelperText>}
        </FormControl>
      );
    }
    return (
      <TextField
        key={field.name}
        fullWidth
        label={`${field.label}${field.required ? ' *' : ''}`}
        placeholder={field.placeholder}
        value={value || ''}
        error={requiredError(field.name)}
        helperText={requiredError(field.name) ? `${field.label} is required` : ''}
        onChange={event => changeSetting(field.name, event.target.value)}
        onBlur={event => changeSetting(field.name, event.target.value.trim())}
      />
    );
  };

  const checkAction = (
    <>
      {file && (
        <Button
          size="small"
          aria-label="Check again"
          title="Check again"
          onClick={() => performCheck(file, trimSettings(settings))}
          sx={{
            gridColumn: { xs: '2', md: 'auto' },
            gridRow: { xs: '1', md: 'auto' },
            justifySelf: 'end',
            minWidth: { xs: 32, md: 'auto' },
            px: { xs: 0.75, md: 1 }
          }}
        >
          <RefreshOutlinedIcon fontSize="small" />
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, ml: 0.5 }}>Check again</Box>
        </Button>
      )}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={previewTab}
        onChange={(_, value) => value && setPreviewTab(value)}
        aria-label="File check view"
        sx={{
          gridColumn: { xs: '1 / -1', md: 'auto' },
          gridRow: { xs: '2', md: 'auto' },
          width: { xs: '100%', md: 'auto' },
          '& .MuiToggleButton-root': { whiteSpace: 'nowrap', flex: { xs: 1, md: 'initial' } }
        }}
      >
        <ToggleButton value="rows">Rows</ToggleButton>
        <ToggleButton value="preview">XML preview</ToggleButton>
      </ToggleButtonGroup>
    </>
  );

  const renderCheckRows = () => {
    if (checking) {
      return <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>;
    }
    if (checkError) {
      return <Alert severity="error">{checkError}</Alert>;
    }
    if (!file) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Choose a file to check its rows before converting.</Typography>
          <Link href={`/api/templates/${type}`} underline="always" sx={{ display: 'inline-block', mt: 1, fontWeight: 700 }}>
            Download the {config.title.toLowerCase()} template
          </Link>
        </Box>
      );
    }
    if (!check) return null;
    return (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: type === 'catalog'
              ? { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(auto-fit, minmax(110px, 1fr))' }
              : 'repeat(3, minmax(0, 1fr))',
            gap: 1,
            mb: 1.75
          }}
        >
          {check.kpis.map(kpi => (
            <Box key={kpi.label} sx={{ p: { xs: 0.75, sm: 1.25 }, border: '1px solid', borderColor: 'divider', borderRadius: '11px' }}>
              <Typography sx={{ fontWeight: 750, fontSize: { xs: 16, sm: 20 }, color: kpi.bad ? 'error.main' : 'text.primary' }}>
                {Number(kpi.value).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 10, sm: 12 }, whiteSpace: 'nowrap' }}>
                {kpi.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            maxHeight: 410,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            p: 0.75
          }}
        >
          <Stack spacing={0.75}>
            {check.rows.map((row, index) => {
              const firstValue = row.cells[0];
              const title = type === 'catalog'
                ? `${row.sheet} · Row ${row.row} · ${firstValue}`
                : `Row ${row.row} · ${firstValue}`;
              const details = type === 'pricebook'
                ? `Price ${row.cells[1]}`
                : type === 'inventory'
                  ? `Quantity ${row.cells[1]}`
                  : row.cells[1] || '—';
              return (
                <Box
                  key={`${row.sheet || type}-${row.row}-${index}`}
                  sx={{
                    minWidth: 0,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '9px',
                    bgcolor: row.error ? '#fff5f5' : '#fff'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, overflowWrap: 'anywhere' }}>
                      {title}
                    </Typography>
                    <Chip
                      size="small"
                      label={row.error ? 'Error' : 'OK'}
                      sx={{
                        flexShrink: 0,
                        bgcolor: row.error ? '#ffe3e3' : '#e6fcf5',
                        color: row.error ? '#c92a2a' : '#087f5b',
                        fontWeight: 700
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, fontSize: 11 }}>
                    {details}
                  </Typography>
                  {row.error && (
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{ display: 'block', mt: 0.25, fontSize: 11, lineHeight: 1.35, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                    >
                      {row.error}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
        <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 410, border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {check.columns.map(column => <TableCell key={column} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{column}</TableCell>)}
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {check.rows.map((row, index) => {
                const values = type === 'catalog'
                  ? [row.sheet, row.row, ...row.cells]
                  : [row.row, ...row.cells];
                return (
                  <TableRow key={`${row.sheet || type}-${row.row}-${index}`} sx={{ bgcolor: row.error ? '#fff5f5' : '#fff' }}>
                    {values.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{cell}</TableCell>
                    ))}
                    <TableCell sx={{ minWidth: 115 }}>
                      <Chip
                        size="small"
                        label={row.error || 'OK'}
                        sx={{
                          maxWidth: 240,
                          bgcolor: row.error ? '#ffe3e3' : '#e6fcf5',
                          color: row.error ? '#c92a2a' : '#087f5b',
                          fontWeight: 700,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {check.hiddenErrorCount > 0 && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            +{check.hiddenErrorCount} more errors not shown
          </Typography>
        )}
      </>
    );
  };

  const renderResult = () => {
    if (type !== 'catalog') {
      return (
        <Stack spacing={1.25}>
          <Button component="a" href={result.outputFile} variant="contained" startIcon={<DownloadOutlinedIcon />}>
            Download XML
          </Button>
          <Button variant="text" onClick={convertAnother}>Convert another file</Button>
        </Stack>
      );
    }
    return (
      <Stack spacing={1}>
        {result.files?.map(output => (
          <Button
            key={output.filename}
            component="a"
            href={output.downloadUrl}
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
          >
            Download {output.type === 'master' ? 'master catalog' : 'storefront catalog'}
          </Button>
        ))}
        {result.zipUrl && (
          <Button component="a" href={result.zipUrl} variant="contained" startIcon={<DownloadOutlinedIcon />}>
            Download both (.zip)
          </Button>
        )}
        <Button variant="text" onClick={convertAnother}>Convert another file</Button>
      </Stack>
    );
  };

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'space-between', gap: 1.25, mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Convert / {config.title}</Typography>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 750 }}>{config.title}</Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          value={type}
          onChange={(_, value) => value && navigate(`/${value}`)}
          size="small"
          aria-label="Converter type"
        >
          {Object.entries(converterConfig).map(([key, item]) => (
            <ToggleButton key={key} value={key}>{item.title}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '380px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <Panel
          title="Settings"
          action={savedSettings ? <Typography variant="caption" color="text.secondary">Saved from last time</Typography> : null}
        >
          <Box component="form" onSubmit={handleConvert} noValidate>
            <FileDrop file={file} onFileChange={handleFileChange} disabled={checking || converting} />
            <Stack spacing={1.75} sx={{ mt: 2 }}>
              {config.fields.map(renderField)}
            </Stack>
            {convertError && <Alert severity="error" sx={{ mt: 1.5 }}>{convertError}</Alert>}
            {result ? (
              <Box sx={{ mt: 2, p: 1.75, borderRadius: '11px', bgcolor: '#e6fcf5', border: '1px solid #a4e8ce' }}>
                <Typography sx={{ color: '#087f5b', fontWeight: 750, mb: 1.25 }}>Conversion complete</Typography>
                {renderResult()}
              </Box>
            ) : (
              <>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!file || checking || converting || !check || errorCount > 0}
                  startIcon={converting ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ mt: 2.25, py: 1.15 }}
                >
                  {converting
                    ? 'Converting…'
                    : `Convert ${convertCount.toLocaleString()} ${convertUnit}${convertCount === 1 ? '' : 's'}`}
                </Button>
                {errorCount > 0 && (
                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 1 }}>
                    Fix the {errorCount} {errorCount === 1 ? 'row' : 'rows'} with errors in Excel, then add the file again.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Panel>

        <Box sx={{ minWidth: 0 }}>
          <Panel
            title="File check"
            action={checkAction}
            bodySx={{ p: 2 }}
            headerSx={{
              display: { xs: 'grid', md: 'flex' },
              gridTemplateColumns: { xs: '1fr auto', md: 'none' },
              gridTemplateRows: { xs: 'auto auto', md: 'none' },
              columnGap: 1,
              rowGap: { xs: 1, md: 0 },
              px: { xs: 1.5, md: 2.25 },
              py: { xs: 1.25, md: 1.5 }
            }}
            titleSx={{ gridColumn: { xs: '1', md: 'auto' }, gridRow: { xs: '1', md: 'auto' }, whiteSpace: 'nowrap' }}
            actionSx={{ display: { xs: 'contents', md: 'flex' }, alignItems: 'center', gap: 0.5 }}
          >
            {previewTab === 'rows' ? renderCheckRows() : (
              checking ? <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
                : checkError ? <Alert severity="error">{checkError}</Alert>
                  : !file ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">The XML preview appears after you choose a file.</Typography>
                      <Link href={`/api/templates/${type}`} underline="always" sx={{ display: 'inline-block', mt: 1, fontWeight: 700 }}>
                        Download template
                      </Link>
                    </Box>
                  ) : check?.xmlPreview?.map(item => (
                    <Box key={item.label} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                      <Box component="pre" sx={{
                        overflow: 'auto',
                        maxHeight: 380,
                        whiteSpace: 'pre',
                        bgcolor: '#0e2a2f',
                        color: '#b8e0db',
                        p: 1.75,
                        mt: 0.75,
                        mb: 0,
                        borderRadius: '10px',
                        fontSize: 12,
                        lineHeight: 1.6
                      }}>
                        {item.text}
                      </Box>
                    </Box>
                  ))
            )}
          </Panel>
          <Panel
            title="Recent files"
            action={<Typography variant="caption" color="text.secondary">Kept 24 hours</Typography>}
            sx={{ mt: 2 }}
            bodySx={{ p: 0 }}
          >
            <RecentFilesTable files={typeFiles} />
          </Panel>
        </Box>
      </Box>
    </Box>
  );
};

export default ConverterPage;
